import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppState } from "../../state/AppStateContext";
import {
  getTermDetail,
  listClasses,
  listProperties,
  termExists,
} from "../../rdf/model";
import { filterByActiveDatasets } from "../../state/filters";
import { VOCAB } from "../../rdf/vocab";
import { buildHierarchy, getAncestorPath } from "./hierarchy";
import type { TermSummary } from "../../rdf/types";
import { EXPLORER_TABS, explorePath, type ExplorerTab } from "../../state/navigation";
import TabBar from "./TabBar";
import TreeNav from "./TreeNav";
import TermDetail from "./TermDetail";
import OntologiesTab from "./OntologiesTab";
import FilesTab from "./FilesTab";

const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

const VALID_TABS = new Set(EXPLORER_TABS.map((t) => t.id));

export default function ExplorerPage() {
  const { tab: rawTab } = useParams<{ tab: string }>();
  const tab: ExplorerTab = VALID_TABS.has(rawTab as ExplorerTab)
    ? (rawTab as ExplorerTab)
    : "classes";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { modelContext, datasets, activeDatasetIds, setDatasetActive } = useAppState();
  const [expandedIris, setExpandedIris] = useState<Set<string>>(new Set());

  const selectedIri = searchParams.get("term");

  const classesTerms = useMemo(
    () => (modelContext ? filterByActiveDatasets(listClasses(modelContext), activeDatasetIds) : []),
    [modelContext, activeDatasetIds],
  );
  const propertiesTerms = useMemo(
    () => (modelContext ? filterByActiveDatasets(listProperties(modelContext), activeDatasetIds) : []),
    [modelContext, activeDatasetIds],
  );

  const treeTerms = tab === "properties" ? propertiesTerms : classesTerms;
  const relationPredicateIri =
    tab === "properties" ? VOCAB.rdfs.subPropertyOf : VOCAB.rdfs.subClassOf;

  const hierarchy = useMemo(
    () =>
      modelContext && (tab === "classes" || tab === "properties")
        ? buildHierarchy(modelContext, treeTerms, relationPredicateIri)
        : [],
    [modelContext, treeTerms, relationPredicateIri, tab],
  );

  useEffect(() => {
    if (!modelContext || !selectedIri) return;
    const termsByIri = new Map(treeTerms.map((t) => [t.iri, t]));
    if (!termsByIri.has(selectedIri)) return;
    const ancestors = getAncestorPath(modelContext, selectedIri, relationPredicateIri, termsByIri);
    if (ancestors.length === 0) return;
    setExpandedIris((previous) => new Set([...previous, ...ancestors]));
    // Only re-run when the selection or tab actually changes — expandedIris itself must stay out of the deps to avoid re-collapsing on every toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelContext, selectedIri, tab]);

  const detail =
    modelContext && selectedIri && termExists(modelContext, selectedIri)
      ? getTermDetail(modelContext, selectedIri)
      : null;

  const handleSelectTerm = (term: TermSummary) => {
    navigate(explorePath(term));
  };

  const handleToggleExpand = (iri: string) => {
    setExpandedIris((previous) => {
      const next = new Set(previous);
      if (next.has(iri)) next.delete(iri);
      else next.add(iri);
      return next;
    });
  };

  if (!modelContext) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <TabBar activeTab={tab} onSelectTab={(nextTab) => navigate(`/explore/${nextTab}`)} />

      {tab === "ontologies" ? (
        <OntologiesTab
          ctx={modelContext}
          datasets={datasets}
          activeDatasetIds={activeDatasetIds}
          onSetDatasetActive={setDatasetActive}
        />
      ) : tab === "files" ? (
        <FilesTab datasets={datasets} />
      ) : (
        <div className="flex min-h-0 flex-1">
          <div className="flex w-80 shrink-0 flex-col border-r border-[var(--oe-border)] bg-[var(--oe-surface)]">
            <div className="flex-1 overflow-y-auto">
              <TreeNav
                nodes={hierarchy}
                selectedIri={selectedIri}
                expandedIris={expandedIris}
                onToggleExpand={handleToggleExpand}
                onSelect={handleSelectTerm}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-8">
            {detail ? (
              <TermDetail ctx={modelContext} detail={detail} datasets={datasets} />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                <p className="text-sm font-medium text-[var(--oe-text)]">
                  Select a term from the {tab === "properties" ? "property" : "class"} hierarchy
                </p>
                <p className="max-w-sm text-sm text-[var(--oe-text-muted)]">
                  Or press{" "}
                  <kbd className="rounded border border-[var(--oe-border)] bg-[var(--oe-surface)] px-1.5 py-0.5 font-sans text-[10px] font-medium">
                    {IS_MAC ? "⌘K" : "Ctrl K"}
                  </kbd>{" "}
                  to search across every loaded ontology.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
